// no-dd-sa:typescript-best-practices/boolean-prop-naming
// Tool metadata registry
// Free tools for SEO and lead generation

export type ToolCategory = 'vp-director' | 'engineering-manager' | 'tech-lead';

export interface Tool {
  slug: string;
  title: string;
  description: string;
  metaDescription: string;
  category: ToolCategory;
  estimatedTime: string; // e.g., "2 min"
  featured?: boolean;
  relatedGuides: string[];
  targetKeywords: string[];
  icon?: string; // Lucide icon name
  ogImage?: string; // Custom OG image for social sharing
  relatedFeatures?: string[]; // Feature page slugs for internal linking
  updatedAt?: string; // YYYY-MM-DD for sitemap lastmod
}

export const TOOL_CATEGORIES: Record<ToolCategory, { label: string; description: string; color: string }> = {
  'vp-director': {
    label: 'For VPs & Directors',
    description: 'Strategic metrics and ROI tools for engineering leadership',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  'engineering-manager': {
    label: 'For Engineering Managers',
    description: 'Team performance and planning tools',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  },
  'tech-lead': {
    label: 'For Tech Leads',
    description: 'Code quality and risk assessment tools',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  },
};

export const tools: Tool[] = [
  {
    slug: 'dora-calculator',
    title: 'DORA Metrics Calculator',
    description: 'Calculate your DORA metrics performance tier. Input your deployment frequency, lead time, MTTR, and change failure rate to see how you compare to elite teams.',
    metaDescription: 'Free DORA metrics calculator. Enter deployment frequency, lead time, MTTR, and change failure rate to see your tier in minutes.',
    category: 'vp-director',
    estimatedTime: '2 min',
    featured: true,
    relatedGuides: ['dora-metrics-guide', 'engineering-roi-calculator', 'developer-productivity-score', 'dpe-maturity-assessment'],
    targetKeywords: [
      'DORA metrics calculator',
      'deployment frequency calculator',
      'lead time for changes calculator',
      'change failure rate calculator',
      'MTTR calculator',
      'DORA performance tier',
      'DevOps metrics calculator',
    ],
    icon: 'Gauge',
    ogImage: '/images/og-tools/dora-calculator.webp',
    relatedFeatures: ['dora-metrics', 'deployment-analytics'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'engineering-roi-calculator',
    title: 'Engineering Headcount ROI Calculator',
    description: 'Calculate the ROI of your engineering team. See the true cost per engineer and what productivity gains would mean for your bottom line.',
    metaDescription: 'Free engineering ROI calculator. See true cost per engineer, productivity upside, and payback on headcount or tooling.',
    category: 'vp-director',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['engineering-efficiency-guide', 'dora-calculator', 'developer-productivity-score', 'dpe-maturity-assessment'],
    targetKeywords: [
      'engineering cost calculator',
      'developer ROI calculator',
      'engineering headcount calculator',
      'software engineer cost',
      'engineering productivity ROI',
    ],
    icon: 'DollarSign',
    ogImage: '/images/og-tools/engineering-roi-calculator.webp',
    relatedFeatures: ['investment-allocation', 'executive-summary'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'meeting-cost-calculator',
    title: 'Meeting Cost Calculator',
    description: 'See the true cost of meetings for your engineering team. Calculate how much engineering time and money is spent in meetings.',
    metaDescription: 'Free meeting cost calculator for engineering teams. See weekly hours and dollars lost to meetings and reclaim time.',
    category: 'engineering-manager',
    estimatedTime: '2 min',
    featured: false,
    relatedGuides: ['cycle-time-calculator', 'code-review-time-estimator', 'team-capacity-planner', 'productivity-improvement-planner'],
    targetKeywords: [
      'meeting cost calculator',
      'meeting ROI calculator',
      'engineering meeting cost',
      'developer time in meetings',
      'meeting time calculator',
    ],
    icon: 'Clock',
    ogImage: '/images/og-tools/meeting-cost-calculator.webp',
    relatedFeatures: ['cycle-time', 'forecasting'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'pr-size-analyzer',
    title: 'PR Size Analyzer',
    description: 'Analyze your pull request sizes and get recommendations. Learn the optimal PR size for faster reviews and fewer bugs.',
    metaDescription: 'Free PR size analyzer. Compare your average PR size to research-backed benchmarks and get review-speed tips.',
    category: 'tech-lead',
    estimatedTime: '2 min',
    featured: false,
    relatedGuides: ['reduce-pr-cycle-time', 'bus-factor-calculator', 'tech-debt-calculator', 'space-framework-assessment'],
    targetKeywords: [
      'optimal PR size',
      'pull request size guide',
      'PR size best practices',
      'code review size',
      'pull request lines of code',
    ],
    icon: 'GitPullRequest',
    ogImage: '/images/og-tools/pr-size-analyzer.webp',
    relatedFeatures: ['risky-changes', 'cycle-time'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'cycle-time-calculator',
    title: 'Cycle Time Calculator',
    description: 'Calculate your PR cycle time breakdown. See where time is spent between coding, waiting for review, in review, and merging.',
    metaDescription: 'Free PR cycle time calculator. Break down coding, wait time, review, and merge to find the bottleneck fast.',
    category: 'engineering-manager',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['reduce-pr-cycle-time', 'meeting-cost-calculator', 'code-review-time-estimator', 'team-capacity-planner'],
    targetKeywords: [
      'PR cycle time calculator',
      'cycle time formula',
      'how to calculate cycle time',
      'cycle time calculation',
      'code review cycle time',
      'pull request lead time',
      'cycle time breakdown',
      'development cycle time',
    ],
    icon: 'Timer',
    ogImage: '/images/og-tools/cycle-time-calculator.webp',
    relatedFeatures: ['cycle-time', 'forecasting'],
    updatedAt: '2026-07-21',
  },
  {
    slug: 'bus-factor-calculator',
    title: 'Bus Factor Calculator',
    description: 'Calculate your team\'s bus factor and knowledge concentration risk. Identify critical knowledge silos before they become problems.',
    metaDescription: 'Free bus factor calculator. Identify knowledge silos and single points of failure before they hurt delivery.',
    category: 'tech-lead',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['code-hotspots-knowledge-silos', 'tech-lead-metrics-guide', 'engineering-onboarding-git-data', 'pr-size-analyzer'],
    targetKeywords: [
      'bus factor calculator',
      'knowledge silo risk',
      'truck factor calculator',
      'team knowledge concentration',
      'single point of failure engineering',
    ],
    icon: 'Users',
    ogImage: '/images/og-tools/bus-factor-calculator.webp',
    relatedFeatures: ['knowledge-silos', 'file-hotspots'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'code-review-time-estimator',
    title: 'Code Review Time Estimator',
    description: 'Estimate how long code reviews should take based on PR size and complexity. Set realistic review SLAs for your team.',
    metaDescription: 'Free code review time estimator. Set realistic SLAs based on PR size, complexity, and reviewer load.',
    category: 'engineering-manager',
    estimatedTime: '2 min',
    featured: false,
    relatedGuides: ['reduce-pr-cycle-time', 'meeting-cost-calculator', 'cycle-time-calculator', 'team-capacity-planner'],
    targetKeywords: [
      'code review time estimate',
      'PR review time',
      'code review SLA',
      'review time calculator',
      'pull request review duration',
    ],
    icon: 'Eye',
    ogImage: '/images/og-tools/code-review-time-estimator.webp',
    relatedFeatures: ['review-network', 'review-insights'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'team-capacity-planner',
    title: 'Team Capacity Planner',
    description: 'Calculate your team\'s actual coding capacity. Account for meetings, holidays, and other overhead to plan sprints realistically.',
    metaDescription: 'Meetings eat 23% of engineering time on average. This calculator shows your team\'s real sprint capacity after all overhead is accounted for.',
    category: 'engineering-manager',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['meeting-cost-calculator', 'cycle-time-calculator', 'code-review-time-estimator', 'productivity-improvement-planner'],
    targetKeywords: [
      'sprint capacity calculator',
      'engineering capacity planner',
      'team capacity calculator',
      'developer availability calculator',
      'sprint planning calculator',
    ],
    icon: 'Calendar',
    ogImage: '/images/og-tools/team-capacity-planner.webp',
    relatedFeatures: ['forecasting', 'sprint-retro'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'tech-debt-calculator',
    title: 'Technical Debt Cost Calculator',
    description: 'Quantify the cost of technical debt. Calculate how much productivity you\'re losing and build a case for refactoring.',
    metaDescription: 'Put a dollar amount on technical debt. Calculate productivity drag, accumulated interest, and the exact ROI of your refactoring proposal.',
    category: 'tech-lead',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['pr-size-analyzer', 'bus-factor-calculator', 'space-framework-assessment', 'ai-productivity-calculator'],
    targetKeywords: [
      'technical debt calculator',
      'tech debt cost',
      'refactoring ROI calculator',
      'technical debt quantification',
      'cost of technical debt',
    ],
    icon: 'AlertTriangle',
    ogImage: '/images/og-tools/tech-debt-calculator.webp',
    relatedFeatures: ['file-hotspots', 'knowledge-silos'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'developer-productivity-score',
    title: 'Developer Productivity Score Calculator',
    description: 'Calculate your team\'s productivity score across three dimensions: Efficiency, Effectiveness, and Experience. Get personalized recommendations.',
    metaDescription: 'Free developer productivity score. Measure efficiency, effectiveness, and experience with actionable recommendations.',
    category: 'vp-director',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['what-is-developer-productivity', 'developer-productivity-metrics-guide', 'space-framework-metrics-guide', 'dora-calculator'],
    targetKeywords: [
      'developer productivity',
      'what is developer productivity',
      'developer productivity score',
      'productivity calculator',
      'engineering productivity assessment',
    ],
    icon: 'BarChart3',
    ogImage: '/images/og-tools/developer-productivity-score.webp',
    relatedFeatures: ['developer-comparison'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'productivity-improvement-planner',
    title: 'Developer Productivity Improvement Planner',
    description: 'Select your team\'s pain points and get a prioritized 30-day improvement plan with specific interventions and expected impact.',
    metaDescription: 'Pick your top 3 pain points. Get a prioritized 30-day improvement plan with specific interventions, expected impact, and how to measure success.',
    category: 'engineering-manager',
    estimatedTime: '2 min',
    featured: false,
    relatedGuides: ['improve-developer-productivity-guide', 'reduce-pr-cycle-time', 'review-load-balancing-guide', 'meeting-cost-calculator'],
    targetKeywords: [
      'productivity improvement plan',
      'how to improve developer productivity',
      'improve developer productivity',
      'increase developer productivity',
      'developer productivity improvement',
    ],
    icon: 'ListChecks',
    ogImage: '/images/og-tools/productivity-improvement-planner.webp',
    relatedFeatures: ['cycle-time', 'space-framework'],
    updatedAt: '2026-07-21',
  },
  {
    slug: 'dpe-maturity-assessment',
    title: 'DPE Maturity Assessment',
    description: 'Assess your organization\'s Developer Productivity Engineering maturity level and get a roadmap to level up. Based on practices at Netflix, Meta, and Spotify.',
    metaDescription: 'Free DPE maturity assessment. Benchmark your developer productivity engineering program and get a roadmap.',
    category: 'vp-director',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['developer-productivity-engineering-guide', 'platform-team-metrics', 'internal-developer-platform-metrics', 'dora-calculator'],
    targetKeywords: [
      'DPE maturity',
      'developer productivity engineering',
      'DPE assessment',
      'productivity engineering',
      'DPE team',
    ],
    icon: 'Layers',
    ogImage: '/images/og-tools/dpe-maturity-assessment.webp',
    relatedFeatures: ['dora-metrics', 'deployment-analytics'],
    updatedAt: '2026-07-21',
  },
  {
    slug: 'productivity-tool-recommender',
    title: 'Developer Productivity Tool Recommender',
    description: 'Get personalized tool recommendations based on your team size, pain points, and budget. Compare tool categories for developer productivity.',
    metaDescription: 'Stop Googling tool comparisons. Answer 5 questions about your team and get matched to the right productivity tooling for your situation.',
    category: 'engineering-manager',
    estimatedTime: '2 min',
    featured: false,
    relatedGuides: ['developer-productivity-tools-guide', 'engineering-analytics-tools-comparison', 'developer-tooling-roi-guide', 'meeting-cost-calculator'],
    targetKeywords: [
      'best developer productivity tools',
      'developer productivity tools',
      'software developer productivity tools',
      'tools to increase developer productivity',
      'productivity tool comparison',
    ],
    icon: 'Wrench',
    ogImage: '/images/og-tools/productivity-tool-recommender.webp',
    relatedFeatures: ['ai-tools-roi', 'executive-summary'],
    updatedAt: '2026-07-21',
  },
  {
    slug: 'devops-roi-calculator',
    title: 'DevOps ROI Calculator',
    description: 'Calculate the return on investment for DevOps transformation. Quantify deployment automation savings, incident reduction, and productivity gains.',
    metaDescription: 'Free DevOps ROI calculator. Estimate automation savings, incident reduction, and productivity gains.',
    category: 'vp-director',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['devops-transformation-guide', 'dora-metrics-guide', 'devops-metrics-kpis-guide', 'engineering-metrics-business-case'],
    targetKeywords: [
      'devops roi calculator',
      'devops benefits',
      'advantages of devops',
      'why devops',
      'devops business case',
      'devops cost savings',
    ],
    icon: 'Calculator',
    ogImage: '/images/og-tools/devops-roi-calculator.webp',
    relatedFeatures: ['dora-metrics', 'deployment-analytics'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'developer-time-lost-calculator',
    title: 'Developer Time Lost Calculator',
    description: 'Calculate how much time your team loses to context switching, tool sprawl, meetings, and interruptions. See the true cost of productivity drains.',
    metaDescription: 'Free time-lost calculator. Quantify context switching, tool sprawl, and interruptions in hours and FTEs.',
    category: 'vp-director',
    estimatedTime: '3 min',
    featured: true,
    relatedGuides: ['context-switching-patterns-guide', 'engineering-productivity-guide', 'daily-standup-best-practices', 'developer-productivity-tools-guide'],
    targetKeywords: [
      'developer context switching cost',
      'tool sprawl productivity',
      'developer time lost',
      'context switching calculator',
      'developer productivity loss',
      'engineering time waste',
    ],
    icon: 'Clock',
    ogImage: '/images/og-tools/developer-time-lost-calculator.webp',
    relatedFeatures: ['cycle-time', 'space-framework'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'ai-productivity-calculator',
    title: 'AI Coding Assistant ROI Calculator',
    description: 'Calculate the ROI of AI coding assistants like GitHub Copilot, Cursor, and Claude. See productivity gains, cost savings, and payback period.',
    metaDescription: 'Free AI coding assistant ROI calculator. Estimate gains from Copilot, Cursor, or Claude and payback period.',
    category: 'engineering-manager',
    estimatedTime: '2 min',
    featured: false,
    relatedGuides: ['developer-productivity-tools-guide', 'engineering-efficiency-guide', 'engineering-productivity-guide', 'pr-size-analyzer'],
    targetKeywords: [
      'GitHub Copilot ROI',
      'AI coding assistant productivity',
      'Copilot productivity gain',
      'AI developer tools ROI',
      'coding assistant calculator',
      'AI developer productivity',
    ],
    icon: 'Bot',
    ogImage: '/images/og-tools/ai-productivity-calculator.webp',
    relatedFeatures: ['ai-tools-roi'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'space-framework-assessment',
    title: 'SPACE Framework Assessment',
    description: 'Assess your team across all 5 SPACE dimensions: Satisfaction, Performance, Activity, Communication, and Efficiency. Get a visual radar chart and recommendations.',
    metaDescription: 'Score your team across all 5 SPACE dimensions in 3 minutes. Get a radar chart visualization plus specific recommendations for your weakest areas.',
    category: 'tech-lead',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['space-framework-metrics-guide', 'developer-productivity-metrics-guide', 'what-is-developer-productivity', 'pr-size-analyzer'],
    targetKeywords: [
      'SPACE framework',
      'SPACE productivity framework',
      'developer productivity framework',
      'SPACE vs DORA',
      'SPACE framework assessment',
      'Nicole Forsgren SPACE',
    ],
    icon: 'Radar',
    ogImage: '/images/og-tools/space-framework-assessment.webp',
    relatedFeatures: ['space-framework'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'analytics-roi-calculator',
    title: 'Engineering Analytics ROI Calculator',
    description: 'Calculate the ROI of engineering analytics tools. See hidden capacity from wait time, cycle time improvement value, and reporting time savings.',
    metaDescription: 'Free engineering analytics ROI calculator. Calculate hidden capacity recovery, cycle time value, and reporting savings for your team.',
    category: 'vp-director',
    estimatedTime: '3 min',
    featured: true,
    relatedGuides: ['engineering-analytics-roi-guide', 'engineering-metrics-business-case', 'engineering-analytics-tools-comparison', 'reduce-pr-cycle-time'],
    targetKeywords: [
      'engineering analytics ROI calculator',
      'developer analytics ROI calculator',
      'engineering metrics tool ROI',
      'cycle time ROI',
      'engineering visibility value',
      'analytics tool business case',
      'engineering tooling ROI',
    ],
    icon: 'Calculator',
    ogImage: '/images/og-tools/analytics-roi-calculator.webp',
    relatedFeatures: ['executive-summary', 'investment-allocation'],
    updatedAt: '2026-07-21',
  },
  {
    slug: 'developer-productivity-assessment',
    title: 'Developer Productivity Assessment',
    description: 'Self-assess your team\'s productivity across delivery, quality, collaboration, and developer experience. Compare to industry benchmarks and get prioritized recommendations.',
    metaDescription: 'Free team productivity assessment. Score your team across 14 questions, compare to benchmarks, and get specific improvement recommendations.',
    category: 'vp-director',
    estimatedTime: '5 min',
    featured: false,
    relatedGuides: ['developer-productivity-metrics-guide', 'space-framework-metrics-guide', 'dora-metrics-guide', 'what-is-developer-productivity'],
    targetKeywords: [
      'developer productivity assessment',
      'team productivity assessment',
      'engineering team assessment',
      'developer productivity benchmark',
      'team productivity score',
      'engineering productivity evaluation',
      'developer productivity metrics',
    ],
    icon: 'ClipboardCheck',
    ogImage: '/images/og-tools/developer-productivity-assessment.webp',
    relatedFeatures: ['developer-comparison', 'space-framework', 'executive-summary'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'github-org-analyzer',
    title: 'GitHub Organization Analyzer',
    description: 'Analyze any GitHub organization to estimate team size, activity levels, and engineering health. See contributor counts, active repos, tech stack, and more.',
    metaDescription: 'Free GitHub org analyzer. Estimate team size, view activity metrics, and assess engineering health for any public GitHub organization.',
    category: 'vp-director',
    estimatedTime: '1 min',
    featured: false,
    relatedGuides: ['dora-metrics-guide', '2025-engineering-benchmarks', 'developer-productivity-metrics-guide'],
    targetKeywords: [
      'github organization analyzer',
      'github team size',
      'github org metrics',
      'engineering team analysis',
      'github activity metrics',
      'github org contributors',
      'analyze github organization',
    ],
    icon: 'Github',
    relatedFeatures: ['dora-metrics', 'review-network'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'github-bus-factor-checker',
    title: 'GitHub Bus Factor Checker',
    description: 'Paste any public GitHub repo and get its bus factor: how few people wrote half the commits. A real, live read on how concentrated a codebase\'s knowledge is.',
    metaDescription: 'Free GitHub bus factor checker. See how many people wrote half a repo\'s commits and how concentrated its knowledge is - live from the public GitHub API.',
    category: 'tech-lead',
    estimatedTime: '1 min',
    featured: false,
    relatedGuides: ['dora-metrics-guide', 'developer-productivity-metrics-guide'],
    targetKeywords: [
      'github bus factor',
      'bus factor calculator',
      'repository bus factor',
      'knowledge silo risk',
      'code ownership concentration',
      'bus factor github repo',
    ],
    icon: 'ShieldAlert',
    relatedFeatures: ['knowledge-silos', 'file-hotspots', 'review-network'],
    updatedAt: '2026-07-18',
  },
  {
    slug: 'pricing-comparison-calculator',
    title: 'Engineering Analytics Pricing Calculator',
    description: 'Compare the annual cost of engineering analytics platforms side-by-side. Input your team size and see how per-seat pricing stacks up against flat-rate alternatives across 10 tools.',
    metaDescription: 'Free pricing calculator for engineering analytics tools. Compare LinearB, Jellyfish, Swarmia, Sleuth, and more by team size. See annual costs and 3-year TCO.',
    category: 'vp-director',
    estimatedTime: '2 min',
    featured: true,
    relatedGuides: ['engineering-analytics-tools-comparison', 'engineering-metrics-business-case', 'analytics-roi-calculator', 'engineering-roi-calculator'],
    targetKeywords: [
      'engineering analytics pricing comparison',
      'engineering analytics cost',
      'hatica pricing',
      'developer analytics pricing',
      'engineering management platform cost',
    ],
    icon: 'DollarSign',
    relatedFeatures: ['investment-allocation', 'executive-summary'],
    updatedAt: '2026-07-21',
  },
  {
    slug: 'pr-pulse-chrome-extension',
    title: 'PR Pulse: GitHub PR Age & Complexity Extension',
    description: 'Free Chrome extension showing PR age badges and complexity scores on GitHub. Color-coded indicators help spot stale PRs instantly. Zero data collection, fully customizable.',
    metaDescription: 'Free Chrome extension for GitHub. See PR age (green <24h, yellow 1-3d, red >3d) and complexity at a glance. No data collection.',
    category: 'tech-lead',
    estimatedTime: '1 min install',
    featured: false,
    relatedGuides: ['reduce-pr-cycle-time', 'code-reviewer-best-practices', 'pr-size-optimization-guide'],
    targetKeywords: [
      'github pr chrome extension',
      'github pull request age badge',
      'pr complexity indicator',
      'stale pr detection',
      'github code review extension',
      'pr age tracker',
      'github pr tools',
    ],
    icon: 'Zap',
    ogImage: '/images/og-tools/pr-pulse-chrome-extension.webp',
    relatedFeatures: ['cycle-time', 'risky-changes'],
    updatedAt: '2026-06-09',
  },
  {
    slug: 'working-agreement-generator',
    ogImage: '/images/og-tools/working-agreement-generator.webp',
    title: 'Working Agreement Generator',
    description: 'Generate a working agreement your team will actually keep. Toggle eight proven clauses - PR size, review turnaround, focus time - edit the numbers, and download the markdown.',
    metaDescription: 'Free working agreement generator. Toggle 8 proven clauses, set your own limits, and download a markdown doc your team can commit to the repo.',
    category: 'engineering-manager',
    estimatedTime: '3 min',
    featured: false,
    relatedGuides: ['working-agreements-guide', 'swarmia-alternative', 'code-reviewer-best-practices'],
    targetKeywords: [
      'working agreement template',
      'team working agreement generator',
      'engineering team working agreement',
      'working agreement examples',
      'agile working agreement template',
    ],
    icon: 'ListChecks',
    relatedFeatures: ['cycle-time', 'review-insights'],
    updatedAt: '2026-07-12',
  },
];

export function getToolBySlug(slug: string): Tool | undefined {
  return tools.find((t) => t.slug === slug);
}

export function getToolsByCategory(category: ToolCategory): Tool[] {
  return tools.filter((t) => t.category === category);
}

export function getFeaturedTool(): Tool | undefined {
  return tools.find((t) => t.featured);
}

export function getRelatedTools(slug: string): Tool[] {
  const tool = getToolBySlug(slug);
  if (!tool) return [];
  // Return tools in the same category (excluding current tool)
  return tools.filter((t) => t.category === tool.category && t.slug !== slug).slice(0, 3);
}